/**
 * Customer Delete Action Handler (single-tenant)
 *
 * A11: deletion targets exactly ONE tenant. A single name-confirmation input
 * cannot produce N distinct tenant confirmations, so there is no bulk delete
 * path here. This calls the real `useCreateTenantDeletion` mutation
 * (POST /account/deletion) — replacing the former no-op that set
 * `customer_type = 'DELETE'`.
 *
 * Targeting is via the request body (`tenant` + server-verified
 * `nameConfirmation`), never the impersonation header; the mutation already
 * runs non-impersonated and invalidates the deletion list on success. Backend
 * refusals (403 gate; 409 denylist / subtenant / name-mismatch / lock) are
 * surfaced verbatim by the mutation (`getErrorFromResponse: true`).
 */

import {
  type CreateTenantDeletionPayload,
  type DeletionRecord,
  useCreateTenantDeletion,
} from '@/hooks/useTenantDeletionMutations';

/**
 * Hook for the single-tenant deletion request.
 *
 * @returns `handleDeleteCustomer` (requests deletion of one tenant) + `isPending`
 *
 * @example
 * ```tsx
 * const { handleDeleteCustomer } = useCustomerDeleteHandlers();
 * await handleDeleteCustomer({
 *   tenant: customer.email,
 *   mode: 'scheduled',
 *   graceWindowDays: 7,
 *   nameConfirmation: typedValue,
 * });
 * ```
 */
export const useCustomerDeleteHandlers = () => {
  const createDeletion = useCreateTenantDeletion();

  /**
   * Requests deletion of a single tenant.
   *
   * Rejects (throws the normalized API error, with the backend message on
   * `.name`) on any backend refusal so the caller can surface it verbatim.
   *
   * @param payload - `{ tenant, mode, graceWindowDays?, nameConfirmation }`
   */
  const handleDeleteCustomer = async (
    payload: CreateTenantDeletionPayload
  ): Promise<DeletionRecord> => {
    return createDeletion.mutateAsync(payload);
  };

  return {
    handleDeleteCustomer,
    isPending: createDeletion.isPending,
  };
};
